import React from 'react';
import {Image as NativeImage, View, ViewStyle} from 'react-native';
import {Card, Image} from '@rneui/base';
import {makeStyles} from '@rneui/themed';

import brandlogo from '../../../images/brand/header-logo.png';
import {formatMemberSinceDate} from '../../../utils/date';
import Text from '../../themed/Text';
import {REWARDS_BANNER_ASPECT_RATIO, rewardsBranding} from '../rewardsBranding';

import Barcode from './Barcode';

const useStyles = makeStyles(theme => {
  return {
    wrapperStyle: {
      alignItems: 'center',
    },
    containerStyle: {
      margin: 0,
      borderRadius: 20,
      // DIS-1765: per-brand card surface, matched to the banner's edge colour.
      backgroundColor: rewardsBranding.cardSurface,
      borderWidth: 0,
    },
    banner: {
      width: '82%',
      height: undefined,
      aspectRatio: REWARDS_BANNER_ASPECT_RATIO,
      marginTop: 12,
    },
    barcodeContainerStyle: {
      backgroundColor: theme.colors.white,
      borderRadius: theme.size.s2,
      padding: theme.size.s2,
      alignSelf: 'stretch',
      alignContent: 'center',
      marginTop: 16,
    },
    memberName: {
      color: rewardsBranding.cardText,
      fontWeight: 'bold',
      alignSelf: 'flex-start',
      marginTop: 10,
    },
    staffPill: {
      alignSelf: 'flex-start',
      marginTop: 10,
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 999,
      // DIS-1239: invert the on-card color pair so the pill always contrasts
      // with the card surface.
      backgroundColor: rewardsBranding.cardText,
    },
    staffPillText: {
      // Pill text = card bg color so the pill reads as an inverted chip.
      color: rewardsBranding.cardSurface,
      fontSize: 11,
      fontWeight: 'bold',
      letterSpacing: 1.2,
      lineHeight: 14,
    },
    memberSince: {
      color: rewardsBranding.cardText,
      fontSize: 12,
      alignSelf: 'flex-start',
      marginTop: 4,
    },
  };
});

// DIS-1861: stretches the card's width so its lines start at the left edge,
// and takes any spare height above it so it sits at the bottom. Theme-free, so
// it lives outside makeStyles.
const footerStyle: ViewStyle = {
  alignSelf: 'stretch',
  alignItems: 'flex-start',
  marginTop: 'auto',
};

type MemberCardProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logoImg?: any;
  cardNumber: string;
  name: string;
  pending?: boolean;
  memberSince?: string;
  isStaff?: boolean;
  isLmgStaff?: boolean;
  children?: ({color}: {color: string}) => React.ReactNode;
};

export default function MemberCard({
  logoImg = brandlogo,
  cardNumber = 'Example 1234',
  name,
  pending,
  memberSince,
  isStaff,
  isLmgStaff,
  children,
}: MemberCardProps) {
  const styles = useStyles();
  return (
    <Card wrapperStyle={styles.wrapperStyle} containerStyle={styles.containerStyle}>
      {!!logoImg && (
        <Image
          containerStyle={{justifyContent: 'center', alignContent: 'center'}}
          source={logoImg}
          style={{
            width: 120,
            height: 32,
          }}
          resizeMode="contain"
        />
      )}

      {/* DIS-1861: the banner, or Thirsty Camel's Camel Club roundel, which
          `contain` centres in the same box at the box's height. */}
      <NativeImage
        testID="member-card-banner"
        source={rewardsBranding.cardBadge}
        style={styles.banner}
        resizeMode="contain"
        accessibilityIgnoresInvertColors
      />

      <View style={[styles.barcodeContainerStyle]}>
        <View style={{alignItems: 'center'}}>
          <Barcode value={cardNumber} height={42} />
        </View>

        <View style={{marginTop: 10, alignItems: 'center'}}>
          {pending ? (
            <Text variant="body2" style={{fontStyle: 'italic'}}>
              Pending...
            </Text>
          ) : (
            <Text>{cardNumber}</Text>
          )}
        </View>
      </View>
      {/* DIS-1861: name and "Member since" anchored bottom-left. */}
      <View style={footerStyle} testID="member-card-footer">
        {isLmgStaff ? (
          <View style={styles.staffPill}>
            <Text style={styles.staffPillText}>LMG STAFF</Text>
          </View>
        ) : (
          !!isStaff && (
            <View style={styles.staffPill}>
              <Text style={styles.staffPillText}>STAFF</Text>
            </View>
          )
        )}
        <Text style={[styles.memberName]}>{name}</Text>
        {!!memberSince && (
          <Text style={[styles.memberSince]}>
            {`Member since ${formatMemberSinceDate(memberSince, 'MMMM yyyy')}`}
          </Text>
        )}
      </View>
      {!!pending && (
        <View
          style={{
            position: 'absolute',
            backgroundColor: 'white',
            opacity: 0.5,
            left: -16,
            right: -16,
            top: -16,
            bottom: -16,
          }}
        />
      )}
      {typeof children === 'function' ? children({color: styles.memberName.color}) : children}
    </Card>
  );
}
